import {
	SHOPPER,
	audiencesFor,
	isAvailableTo
} from "@/domain/catalog/audience";
import type { Audience, OnlineStatus } from "@/domain/catalog/online-status";
import type { ProductType } from "@/domain/catalog/product";

const LISTING_AUDIENCES = audiencesFor(SHOPPER);

export const LISTING_TYPES: readonly ProductType[] = [
	"durable",
	"consumable",
	"kit",
	"bundle",
	"subscription"
];

export const LISTING_STATUSES: readonly OnlineStatus[] = [
	"active",
	"out-of-stock",
	"temporarily-unavailable",
	"pre-launch"
];

export interface ListingEligibilityFacts {
	onlineStatus: OnlineStatus;
	availableTo: Audience[];
	notSearchable: boolean;
	isReplacement: boolean;
	virtualSku: boolean;
	type: ProductType;
}

const INDEX_HAS_ELIGIBILITY_FACETS = true;

export interface ListingFilterOptions {
	categoryName: string;
	indexHasEligibilityFacets?: boolean;
}

function quote(value: string): string {
	return `"${value.replace(/"/g, '\\"')}"`;
}

function anyOf(attribute: string, values: readonly string[]): string {
	return `(${values.map((v) => `${attribute}:${quote(v)}`).join(" OR ")})`;
}

export function buildListingFilters({
	categoryName,
	indexHasEligibilityFacets = INDEX_HAS_ELIGIBILITY_FACETS
}: ListingFilterOptions): string {
	const clauses = [`categories.title:${quote(categoryName)}`];

	if (indexHasEligibilityFacets) {
		clauses.push(
			"notSearchable:false",
			"isReplacement:false",
			"virtualSku:false",
			anyOf("availableTo", LISTING_AUDIENCES),
			anyOf("type", LISTING_TYPES),
			anyOf("onlineStatus", LISTING_STATUSES)
		);
	}

	return clauses.join(" AND ");
}

export function isListingEligible(product: ListingEligibilityFacts): boolean {
	if (product.notSearchable) return false;
	if (product.isReplacement) return false;
	if (product.virtualSku) return false;
	if (!LISTING_TYPES.includes(product.type)) return false;
	if (!LISTING_STATUSES.includes(product.onlineStatus)) return false;

	return isAvailableTo(product.availableTo, SHOPPER);
}
